# There are small discrepencies between products stored by Luther and products
# returned from sourcing via the microservice. Until these can be aligned the
# former will be referred to as 'ProtectionProduct' and the latter will be
# referred to as 'SolutionProtectionProduct'

enum Frequency {
  INVALID
  UNDEFINED
  WEEKLY
  MONTHLY
  QUARTERLY
  ANNUALLY
  ONCE
}

enum CoverUnits {
  COVER_UNITS_INVALID
  COVER_UNITS_UNIT_1
  COVER_UNITS_UNIT_2
  COVER_UNITS_UNIT_3
  COVER_UNITS_UNIT_4
  COVER_UNITS_UNIT_5
}

enum PremiumType {
  PREMIUM_TYPE_INVALID
  PREMIUM_TYPE_REVIEWABLE
  PREMIUM_TYPE_GUARANTEED
}

enum PremiumFrequency {
  INVALID_PREMIUM_FREQUENCY
  PREMIUM_FREQUENCY_MONTHLY
  PREMIUM_FREQUENCY_ANNUAL
}

enum PayoutType {
  PAYOUT_TYPE_INVALID
  PAYOUT_TYPE_LIFE
  PAYOUT_TYPE_CIC
  PAYOUT_TYPE_LIFE_OR_EARLIER_CIC
}

enum ProtectionQuickQuoteType {
  QUICK_QUOTE_INVALID
  QUICK_QUOTE_IP
  QUICK_QUOTE_FIB
  QUICK_QUOTE_LIFE
  QUICK_QUOTE_CIC
  QUICK_QUOTE_WOL
  QUICK_QUOTE_PMI
}

enum ProtectionProductType {
  PRODUCT_TYPE_INVALID
  PRODUCT_TYPE_TERM
  PRODUCT_TYPE_LEVEL_TERM
  PRODUCT_TYPE_DECREASING_TERM
  PRODUCT_TYPE_INCOME_PROTECTION
  PRODUCT_TYPE_FAMILY_INCOME_BENEFIT
  PRODUCT_TYPE_WHOLE_OF_LIFE
  PRODUCT_TYPE_MORTGAGE_PROTECTION
  PRODUCT_TYPE_MORTGAGE_PAYMENT_PROTECTION
  PRODUCT_TYPE_SICK_PAY
  PRODUCT_TYPE_PMI
  PRODUCT_TYPE_OTHER
  PRODUCT_TYPE_GROUP_LIFE
  PRODUCT_TYPE_GROUP_PMI
  PRODUCT_TYPE_GROUP_ASU
  PRODUCT_TYPE_GROUP_CIC
  PRODUCT_TYPE_GROUP_IP
  PRODUCT_TYPE_RELEVANT_LIFE
  PRODUCT_KEY_MAN
  PRODUCT_TYPE_PRIVATE_MEDICAL_INSURANCE
  PRODUCT_TYPE_SHAREHOLDER_PROTECTION
  PRODUCT_TYPE_CORE_ACCIDENT_SICKNESS
  PRODUCT_TYPE_CHILD_ACCIDENT_SICKNESS
  PRODUCT_TYPE_SPORTS_LIFESTYLE
  PRODUCT_TYPE_SPECIALIST_HEALTHCARE_COVER
  PRODUCT_TYPE_GROUP_PRIVATE_MEDICAL_INSURANCE
  PRODUCT_TYPE_EMPLOYEE_BENEFIT
  PRODUCT_TYPE_EXECUTIVE_IP
  PRODUCT_TYPE_RELEVANT_LIFE_LEVEL
  PRODUCT_TYPE_RELEVANT_LIFE_DECREASING
}

enum IndexationType {
  INDEXATION_TYPE_INVALID
  INDEXATION_TYPE_LEVEL
  INDEXATION_TYPE_RPI
  INDEXATION_TYPE_AWEI
  INDEXATION_TYPE_FIXED
}

enum ProductAddon {
  PROTECTION_ADD_ON_INVALID
  PROTECTION_ADD_ON_FRACTURE_COVER
  PROTECTION_ADD_ON_WAIVER_OF_PREMIUM
  PROTECTION_ADD_ON_TOTAL_PERMANENT_DISABILITY
}

enum ProtectionProductStatus {
  INVALID_STATUS
  CURRENT
  PROPOSED
  SELECTED
  CANCELLED
  NOT_PROCEEDING
  EXPIRED
}

enum CommissionFrequency {
  INVALID_COMMISSION_FREQUENCY
  MONTHLY
  ANNUAL
}

enum BusinessProtectionBenefitOptions {
  PRODUCT_TYPE_GROUP_LIFE
  PRODUCT_TYPE_GROUP_PMI
  PRODUCT_TYPE_PMI
  PRODUCT_TYPE_GROUP_ASU
  PRODUCT_TYPE_GROUP_CIC
  PRODUCT_TYPE_GROUP_IP
  PRODUCT_TYPE_RELEVANT_LIFE
  PRODUCT_KEY_MAN
  PRODUCT_TYPE_SHAREHOLDER_PROTECTION
  PRODUCT_TYPE_PRIVATE_MEDICAL_INSURANCE
  PRODUCT_TYPE_GROUP_PRIVATE_MEDICAL_INSURANCE
  PRODUCT_TYPE_EMPLOYEE_BENEFIT
  PRODUCT_TYPE_EXECUTIVE_IP
  PRODUCT_TYPE_RELEVANT_LIFE_LEVEL
  PRODUCT_TYPE_RELEVANT_LIFE_DECREASING
}

enum UnderwritingLevel {
  UNDERWRITING_LEVEL_INVALID
  UNDERWRITING_LEVEL_FULL_MEDICAL_UNDERWRITING
  UNDERWRITING_LEVEL_MORATORIUM
  UNDERWRITING_LEVEL_MEDICAL_HISTORY_DISREGARDED
  UNDERWRITING_LEVEL_CONTINUED_PERSONAL_MEDICAL_EXCLUSIONS
  UNDERWRITING_LEVEL_CONTINUED_MORATORIUM
}

enum HospitalList {
  HOSPITAL_LIST_INVALID
  HOSPITAL_LIST_REDUCED
  HOSPITAL_LIST_STANDARD
  HOSPITAL_LIST_EXTENDED
  HOSPITAL_LIST_GUIDED
}

enum CoverAmountType {
  MAXIMUM
  MONTHLY
  INVALID_COVER_AMOUNT_TYPE
}

enum ProtectionProvider {
  IPIPELINE
}

enum ReasonEntity {
  INVALID
  CLIENT
  PROVIDER
  SYSTEM
}

enum EmploymentStatusRiskReality {
  INVALID_EMPLOYMENT
  EMPLOYMENT_EMPLOYED
  EMPLOYMENT_FIXED_TERM_CONTRACTOR
  EMPLOYMENT_FIXED_TERM_CONTRACTOR_PAYE
  EMPLOYMENT_HOMEMAKER
  EMPLOYMENT_RETIRED
  EMPLOYMENT_SELF_EMPLOYED
  INVALID_EMPLOYMENT_STATUS
}

#
# Types
#

type ProtectionEntityVersion {
  protection_id: String!
  version: Int!
}

type BusinessProtectionProducts {
  case_id: String
  protection_products: [ProtectionProduct]
}

type Source {
  integrator_name: String
  product_reference: String
  quote_reference: String
  provider_reference: String
}

type ProtectionProduct {
  protection_id: ID
  created_at: String
  version: String
  version_created_at: String
  details: ProtectionProductDetails!
}

type ExternalProtectionProductSource {
  id: String
  name: ExternalIdNames
}

type ExternalProtectionProduct {
  id: String
  source: ExternalProtectionProductSource
}

type ProtectionProductDetails {
  provider_name: String
  premium_frequency: PremiumFrequency
  start_date: String
  end_date: String
  warnings: [String!]
  errors: [String]
  source: ProtectionProductSource
  product_benefits: ProductBenefit
  commission: ProductCommission
  documents: ProductDocuments
  solution_type: SolutionType
  solution_products: [String]
  cancellation_date: String
  product_name: String
  recommendation_premium_amount: String
  underwritten_premium_amount: String
  product_details: String
  policy_number: String
  source_response: String
  is_employment_benefit: Boolean
  is_business_protection: Boolean
  keep_existing_policy: Boolean
  product_recommendation_justification: String
  status: ProtectionProductStatus
  external: [ExternalProtectionProduct]
  client_ids: [String!]
  case_ids: [String!]
  org_provider_reference: String
  modified_at: String
  product_benefit_group_id: String
  not_proceeding_details: NotProceedingDetails
  recommended_case: ProtectionProductRecommendedCase
  premium_change_reason: String
  is_renewal: Boolean
}

type ProtectionProductSource {
  integrator_name: String
  product_reference: String
  quote_reference: String
  provider_reference: String
}

type ProductBenefit {
  product_type_term: [ProductBenefitItem!]
  product_type_family_income_benefit: [ProductBenefitItem!]
  product_type_whole_of_life: [ProductBenefitItem!]
  product_type_income_protection: [ProductBenefitItem!]
  product_type_other: [ProductBenefitItem!]
  product_type_core_accident_sickness: [ProtectionProductBenefitItem!]
  product_type_child_accident_sickness: [ProtectionProductBenefitItem!]
  product_type_sports_lifestyle: [ProtectionProductBenefitItem!]
  product_type_specialist_healthcare_cover: [ProtectionProductBenefitItem!]
  product_type_group_life: [ProtectionProductBenefitItem!]
  product_type_group_pmi: [ProtectionProductBenefitItem!]
  product_type_group_asu: [ProtectionProductBenefitItem!]
  product_type_group_cic: [ProtectionProductBenefitItem!]
  product_type_group_ip: [ProtectionProductBenefitItem!]
  product_type_relevant_life: [ProtectionProductBenefitItem!]
  product_key_man: [ProtectionProductBenefitItem!]
  product_type_pmi: [BusinessProtectionPMIProductBenefitItem!]
  product_type_shareholder_protection: [ProtectionProductBenefitItem!]
  product_type_private_medical_insurance: [ProtectionProductBenefitItem!]
  product_type_group_private_medical_insurance: [ProtectionProductBenefitItem!]
  product_type_employee_benefit: [ProtectionProductBenefitItem!]
  product_type_executive_ip: [ProtectionProductBenefitItem!]
}

type ProductBenefitItem {
  additional_cover_amount: String
  additional_deferral_period_days: Int
  deferral_period_days: Int
  deferral_period: String
  dual_defer_init_amount: String
  dual_defer_init_defer: Int
  include_limited_benefit_payment_period: Boolean
  indexation: String
  premium_type: PremiumType
  product_type_other_name: String
  product_type_other: String
  product_type: ProtectionProductType!
  client_ids: [String!]
  term_years: Int
  term_age: Int
  cover_amount: String
  cover_amount_type: CoverAmountType
  cover_amount_frequency: Frequency
  cost_amount: String
  cost_amount_frequency: Frequency
  units_of_cover: CoverUnits
  indexation_type: IndexationType
  indexation_percentage: Int
  payout_type: PayoutType
  cover_amount_depreciation_rate: Int
  cover_amount_life: String
  cover_amount_cic: String
  cover_amount_life_or_earlier_cic: String
  product_add_ons: ProductAddons
  excess_amount: String
  underwriting_level: UnderwritingLevel
  private_gp_cover: Boolean
  mental_health: Boolean
  additional_notes: String
  travel_international: Boolean
  six_week_option: Boolean
  outpatient_cover: String
  outpatient_cover_details: String
  hospital_list: HospitalList
  cancer_cover: Boolean
  dental_and_optical: Boolean
  dental: Boolean
  optical: Boolean
  full_diagnostics: Boolean
  claim_in_five_years: Boolean
  claim_details: String
  policy_exclusion_details: String
  therapies: Boolean
  physiotherapy: Boolean
  outpatient_full_cover: Boolean
  limited_payment_plan: Boolean
  age_banded: Boolean
}

type ProtectionProductBenefitItem {
  additional_cover_amount: String
  additional_deferral_period_days: Int
  deferral_period_days: Int
  deferral_period: String
  dual_defer_init_amount: String
  dual_defer_init_defer: Int
  include_limited_benefit_payment_period: Boolean
  indexation: String
  premium_type: PremiumType
  product_type_other_name: String
  product_type_other: String
  product_type: ProtectionProductType
  client_ids: [String!]
  term_years: Int
  term_age: Int
  cover_amount: String
  cover_amount_type: CoverAmountType
  cover_amount_frequency: Frequency
  cost_amount: String
  cost_amount_frequency: Frequency
  units_of_cover: CoverUnits
  indexation_type: IndexationType
  indexation_percentage: Int
  payout_type: PayoutType
  cover_amount_depreciation_rate: Int
  cover_amount_life: String
  cover_amount_cic: String
  cover_amount_life_or_earlier_cic: String
  product_add_ons: ProductAddons
  excess_amount: String
  underwriting_level: UnderwritingLevel
  private_gp_cover: Boolean
  mental_health: Boolean
  additional_notes: String
  travel_international: Boolean
  six_week_option: Boolean
  outpatient_cover: String
  outpatient_cover_details: String
  hospital_list: HospitalList
  cancer_cover: Boolean
  dental_and_optical: Boolean
  dental: Boolean
  optical: Boolean
  full_diagnostics: Boolean
  claim_in_five_years: Boolean
  claim_details: String
  policy_exclusion_details: String
  therapies: Boolean
  physiotherapy: Boolean
  outpatient_full_cover: Boolean
  limited_payment_plan: Boolean
  age_banded: Boolean
}

type BusinessProtectionPMIProductBenefitItem {
  client_ids: [String!]
  cover_amount_frequency: Frequency
  cover_amount: String
  term_years: Int
  excess_amount: String
  underwriting_level: UnderwritingLevel
}

type ProductCommission {
  initial_commission: String
  initial_commission_end_date: String
  commission_frequency: CommissionFrequency
  renewal_commission: String
  renewal_commission_frequency: String
  renewal_commission_start_date: String
  renewal_commission_end_date: String
  description: String
}

type ProductDocuments {
  application_doc_id: String
  key_facts_doc_id: String
  illustration_doc_id: String
  policy_letter_doc_id: String
  comparison_report_doc_id: String
  commission_comparison_report_doc_id: String
}

type NotProceedingDetails {
  reason: String
  reason_entity: ReasonEntity
  had_psr_when_declined: Boolean
}

#
# Inputs
#
input ExternalProtectionProductSourceInput {
  id: String
  name: ExternalIdNames
}

input ExternalProtectionProductInput {
  id: String
  source: ExternalProtectionProductSourceInput
}

input ProtectionProductInput {
  provider_name: String
  premium_frequency: PremiumFrequency
  product_name: String
  recommendation_premium_amount: String
  underwritten_premium_amount: String
  product_details: String
  policy_number: String
  start_date: String
  end_date: String
  cancellation_date: String
  source_response: String
  is_employment_benefit: Boolean
  is_business_protection: Boolean
  keep_existing_policy: Boolean
  product_recommendation_justification: String
  status: ProtectionProductStatus
  client_ids: [String!]
  case_ids: [String!]
  org_provider_reference: String
  source: ProtectionProductSourceInput
  product_add_ons: [ProductAddon!]
  product_benefits: ProductBenefitInput
  commission: ProductCommissionInput
  documents: ProductDocumentsInput
  not_proceeding_details: NotProceedingDetailsInput
  external: [ExternalProtectionProductInput]
  premium_change_reason: String
}

input BusinessProtectionProductsInput {
  case_id: String
  protection_products: [BusinessProtectionProductInput!]
  source_response: String
}

input BusinessProtectionProductInput {
  protection_id: String
  created_at: String
  version: String
  version_created_at: String
  details: BusinessProtectionProductDetailsInput!
}

input BusinessProtectionProductDetailsInput {
  provider_name: String
  premium_frequency: String
  product_name: String
  recommendation_premium_amount: String
  underwritten_premium_amount: String
  product_details: String
  policy_number: String
  start_date: String
  end_date: String
  source_response: String
  is_employment_benefit: Boolean
  is_business_protection: Boolean
  is_renewal: Boolean
  renewal_of: String
  keep_existing_policy: Boolean
  product_recommendation_justification: String
  status: String
  client_ids: [String!]
  case_ids: [String!]
  org_provider_reference: String
  source: ProtectionProductSourceInput
  modified_at: String
  solution_type: String
  product_benefit_group_id: String
  product_benefits: ProductBenefitInput
  cover_amount_life: String
  cover_amount_cic: String
  cover_amount_life_or_earlier_cic: String
}

input ProtectionProductSourceInput {
  integrator_name: String
  product_reference: String
  quote_reference: String
  provider_reference: String
}

input ProductBenefitInput {
  product_type_term: [ProductBenefitItemInput!]
  product_type_family_income_benefit: [ProductBenefitItemInput!]
  product_type_whole_of_life: [ProductBenefitItemInput!]
  product_type_income_protection: [ProductBenefitItemInput!]
  product_type_other: [ProductBenefitItemInput!]
  product_type_group_life: [ProductBenefitGroupInput!]
  product_type_group_pmi: [ProductBenefitGroupInput!]
  product_type_group_asu: [ProductBenefitGroupInput!]
  product_type_group_cic: [ProductBenefitGroupInput!]
  product_type_group_ip: [ProductBenefitGroupInput!]
  product_type_relevant_life: [ProductBenefitRelevantLifeInput!]
  product_key_man: [ProductBenefitGroupInput!]
  product_type_pmi: [ProductBenefitPMIInput!]
  product_type_private_medical_insurance: [ProductBenefitItemInput!]
  product_type_shareholder_protection: [ProductBenefitGroupInput!]
  product_type_core_accident_sickness: [ProductBenefitMetlifeInput!]
  product_type_child_accident_sickness: [ProductBenefitMetlifeInput!]
  product_type_sports_lifestyle: [ProductBenefitMetlifeInput!]
  product_type_specialist_healthcare_cover: [ProductBenefitMetlifeInput!]
  product_type_group_private_medical_insurance: [ProductBenefitItemInput!]
  product_type_employee_benefit: [ProductBenefitItemInput!]
  product_type_executive_ip: [ProductBenefitExecutiveIPInput!]
}

input ProductBenefitItemInput {
  additional_cover_amount: String
  additional_deferral_period_days: Int
  client_ids: [String!]
  cover_amount_depreciation_rate: Int
  cover_amount_life: String
  cover_amount_cic: String
  cover_amount_life_or_earlier_cic: String
  cover_amount_frequency: Frequency
  cost_amount_frequency: Frequency
  cover_amount: String
  cost_amount: String
  cover_amount_type: CoverAmountType
  deferral_period_days: Int
  dual_defer_init_amount: String
  dual_defer_init_defer: Int
  employer_funding_options: EmployerFundingOptionsInput
  include_limited_benefit_payment_period: Boolean
  include_low_start: Boolean
  indexation_percentage: Int
  indexation_type: IndexationType
  payout_type: PayoutType
  premium_type: PremiumType
  premium_frequency: Frequency
  product_add_ons: ProductAddonsInput
  product_benefit_id: String
  product_type_other_name: String
  product_type_other: String
  product_type: ProtectionProductType
  term_years: Int
  term_age: Int
  age_banded: Boolean
  limited_payment_plan: Boolean
  limited_payment_plan_term: Int
  units_of_cover: CoverUnits
  frontend_id: String
  frontend_cost_estimate: String
  frontend_dual_deferred: Boolean
  frontend_indexation: String
  frontend_quickquote_type: ProtectionQuickQuoteType
  deferred_period: DeferredPeriod
  private_gp_cover: Boolean
  mental_health: Boolean
  additional_notes: String
  travel_international: Boolean
  six_week_option: Boolean
  excess_amount: String
  underwriting_level: UnderwritingLevel
  outpatient_cover: String
  outpatient_cover_details: String
  hospital_list: HospitalList
  cancer_cover: Boolean
  dental_and_optical: Boolean
  dental: Boolean
  optical: Boolean
  full_diagnostics: Boolean
  claim_in_five_years: Boolean
  claim_details: String
  policy_exclusion_details: String
  therapies: Boolean
  physiotherapy: Boolean
  outpatient_full_cover: Boolean
  id: String
}

input ProductBenefitPMIInput {
  client_ids: [String!]
  cover_amount_frequency: Frequency
  cover_amount: String
  term_years: Int
  term_age: Int
  excess_amount: String
  underwriting_level: UnderwritingLevel
}

input ProductBenefitGroupInput {
  client_ids: [String!]
  cover_amount: String
  cover_amount_frequency: Frequency
  premium_type: PremiumType
  term_years: Int
  term_age: Int
}

input ProductBenefitRelevantLifeInput {
  client_ids: [String!]
  cover_amount: String
  cover_amount_frequency: Frequency
  cover_amount_life: String
  cover_amount_cic: String
  cover_amount_life_or_earlier_cic: String
  cover_amount_depreciation_rate: Int
  premium_type: PremiumType
  term_years: Int
  term_age: Int
  product_type: ProtectionProductType
  indexation_type: IndexationType
  indexation_percentage: Int
  product_add_ons: ProductAddonsInput
}

input ProductBenefitExecutiveIPInput {
  client_ids: [String!]
  cover_amount: String
  cover_amount_frequency: Frequency
  cover_amount_type: CoverAmountType
  premium_type: PremiumType
  product_type: ProtectionProductType
  term_years: Int
  term_age: Int
  deferral_period_days: Int
  additional_deferral_period_days: Int
  additional_cover_amount: String
  additional_cover_amount_frequency: Frequency
  indexation_type: IndexationType
  indexation_percentage: Int
  include_limited_benefit_payment_period: Boolean
  limited_payment_plan: Boolean
  age_banded: Boolean
  employer_funding_options: EmployerFundingOptionsInput
}

input ProductBenefitMetlifeInput {
  client_ids: [String!]
  cost_amount: String
  cost_amount_frequency: Frequency
  units_of_cover: CoverUnits
}

input ProductCommissionInput {
  initial_commission: String
  initial_commission_end_date: String
  commission_frequency: Frequency
  renewal_commission: String
  renewal_commission_start_date: String
  renewal_commission_frequency: Frequency
  renewal_commission_end_date: String
}

input ProductDocumentsInput {
  application_doc_id: String
  key_facts_doc_id: String
  illustration_doc_id: String
  policy_letter_doc_id: String
  comparison_report_doc_id: String
  commission_comparison_report_doc_id: String
}

input NotProceedingDetailsInput {
  reason: String
  reason_entity: ReasonEntity
}

type ProtectionProductResponse {
  protection_product: ProtectionProduct
}

input ProtectionProductUpdateInput {
  protection_id: String
  details: ProtectionProductInput
}

input ProtectionAuth {
  integrator: ProtectionProvider!
  encrypted_username: String!
  encrypted_password: String!
}

input IressCredentialsPair {
  encrypted_password: String
  user_code: String
  login_id: String
}

input SyncProtectionDocumentsInput {
  product_ids: [String!]
}

input GetRiskRealityInput {
  client_details: [RiskRealityClientDetails!]
  benefit_details: RiskRealityBenefit
}

input RiskRealityBenefit {
  term_years: Int
}

input RiskRealityClientDetails {
  employment_status: EmploymentStatusRiskReality
  occupation_code: String
  income: String
  date_of_birth: String
  is_smoker: Boolean
  first_name: String
  last_name: String
  gender: Gender
  title: Title
}

type GetRiskRealityResponse {
  life: Float
  critical_illness: Float
  income_protection: Float
  any: Float
  life_and_critical_illness: Float
}

type SyncProtectionDocumentsResponse {
  uploads: [ProductDocumentsOutput!]
}

type ProductDocumentsOutput {
  product_id: String!
  document_ids: [String!]
  version: String!
}

input RiskReportInput {
  case_id: String
  client_id: String!
  client_version: Int!
  store_document: Boolean
  term: Int
}

type RiskReportResponse {
  client_hash: String
  document_id: String
  results: RiskReportResults
  term: Int
}

type RiskReportResults {
  loss_of_income_over_term: RiskReportResultsValue
  morbidity_or_mortality_or_loss_of_income_over_term: RiskReportResultsValue
  mortality_over_term: RiskReportResultsValue
  morbidity_or_mortality_over_term: RiskReportResultsValue
}

type RiskReportResultsValue {
  value: Int
}

type ProtectionProductRecommendedCase {
  case_id: String
  created_at: String
}
